import { once } from 'lodash'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import {
  combineLatest,
  distinctUntilChanged,
  Observable,
  skipWhile,
  Subscription,
} from 'rxjs'
import { AlertFactory } from 'alerts/Alert'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { ViewModelList } from 'infra/ViewModelList'
import { shareLatest } from 'utils/rx-util'
import { tapAction, toStream } from 'utils/mobx-util'
import InventoryStore from '../InventoryStore'
import { BundleArchivedFilterOptions } from './BundleRequestQueryFilterBuilder'
import BundleSearchViewModel from './BundleSearchViewModel'
import { BundleStore } from './BundleStore'
import ArchiveBundleViewModel from './views/ArchiveBundleModal/ArchiveBundleViewModel'
import CreateBundleViewModel from './views/CreateBundleDialog/CreateBundleViewModel'
import DuplicateBundleViewModel from './views/DuplicateBundleDialog/DuplicateBundleViewModel'
import EditBundleViewModel from './views/EditBundleDialog/EditBundleViewModel'
import UnarchiveBundleViewModel from './views/UnarchiveBundleModal/UnarchiveBundleViewModel'
import WorkspaceBundleViewModel from './WorkspaceBundleViewModel'
import WorkspaceSearchViewModel from './WorkspaceSearchViewModel'
import WorkspaceSelectorViewModel from './WorkspaceSelectorForBundlesViewModel'
import { BulkDuplicateBundleViewModel } from './views/BulkDuplicateBundleDialog/BulkDuplicateBundleViewModel'

/**
 * Workspace filter for Bundles.
 */
export type BundleManagerWorkspaceFilter = WorkspaceBundles | AllWorkspaces

/**
 * View all bundles in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * View bundles in a workspace.
 */
export interface WorkspaceBundles {
  type: 'Workspace'
  workspace: Workspace
}

/**
 * View model for bundles.
 */
export default class AdminBundleManagerViewModel {
  /**
   * Subscription to workspace selector updates.
   */
  private subscription = new Subscription()

  // private workspaceVMs = new Map<Id, WorkspaceBundleViewModel>()
  private readonly workspaceVMs
  workspaceSearcher = new WorkspaceSearchViewModel(
    this.newAlert,
    this.bundleStore,
  )

  searcher = new BundleSearchViewModel(
    this.bundleStore,
    this.getWorkspaceMemberships,
    this.workspaceSearcher,
    this.createFilterStorage('BundleManagement'),
  )

  workspaceSelectorViewModel = new WorkspaceSelectorViewModel(
    this.getWorkspaceMemberships,
  )

  createBundleViewModel = new CreateBundleViewModel(
    this.inventoryItemStore,
    this.bundleStore,
    this.refreshBundles.bind(this),
    () => this.searcher.workspace,
    this.workspaceSelectorViewModel,
    this.newAlert,
  )

  editBundleViewModel = new EditBundleViewModel(
    this.inventoryItemStore,
    this.bundleStore,
    this.refreshBundles.bind(this),
    () => this.searcher.workspace,
    this.workspaceSelectorViewModel,
    this.newAlert,
  )

  archiveBundleViewModel = new ArchiveBundleViewModel(
    this.bundleStore,
    this.refreshBundles.bind(this),
    this.newAlert,
  )

  unarchiveBundleViewModel = new UnarchiveBundleViewModel(
    this.bundleStore,
    this.refreshBundles.bind(this),
    this.newAlert,
  )

  duplicateBundleViewModel = new DuplicateBundleViewModel(
    this.inventoryItemStore,
    this.bundleStore,
    this.refreshBundles.bind(this),
    () => this.searcher.workspace,
    this.workspaceSelectorViewModel,
    this.newAlert,
  )

  bulkDuplicateBundleViewModel = new BulkDuplicateBundleViewModel(
    this.workspaceSelectorViewModel,
    this.newAlert,
    this.bundleStore,
    this.refreshBundles.bind(this),
  )

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of bundle management.
   */
  private activateSearcher = once(() => this.searcher.activate())

  /**
   * Ensure we only activate the workspace searcher once, as we want to keep it around
   * for subsequent page views of bundle management.
   */
  private activateWorkspaceSearcher = once(() =>
    this.workspaceSearcher.activate(),
  )

  /**
   * Update workspace filter when current workspace is changed.
   */
  private workspaceSelectorUpdates$ = combineLatest([
    this.currentMember$,
    toStream(() => this.searcher.allWorkspaceIdsWithBundles, true),
  ]).pipe(
    skipWhile(([, ids]) => ids.length < 1),
    distinctUntilChanged(
      ([prev], [curr]) => prev.workspace.id === curr.workspace.id,
    ),
    tapAction(([member, ids]) => {
      if (ids.some((id) => id === member.workspace.id)) {
        this.selectWorkspaceForFiltering(member.workspace)
      }
    }),
    shareLatest(),
  )

  constructor(
    private newAlert: AlertFactory,
    private bundleStore: BundleStore,
    private inventoryItemStore: InventoryStore,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private createFilterStorage: FilterStorageFactory,
    private currentMember$: Observable<Member>,
  ) {
    makeObservable(this)

    this.workspaceVMs = new ViewModelList<Workspace, WorkspaceBundleViewModel>({
      create: (workspace) =>
        new WorkspaceBundleViewModel(
          workspace,
          this.searcher,
          () => this.workspaceFilter.type === 'Workspace',
          this.editBundleViewModel,
          this.archiveBundleViewModel,
          this.unarchiveBundleViewModel,
          this.duplicateBundleViewModel,
          this.bulkDuplicateBundleViewModel,
        ),
      models: () => this.workspaces,
    })
  }

  /**
   * The currently selected archived status tab.
   */
  @observable currentArchivedStatusTab = BundleArchivedFilterOptions.ACTIVE

  /**
   * Workspace filter.
   */
  @observable
  workspaceFilter: BundleManagerWorkspaceFilter = {
    type: 'Workspace',
    workspace: Workspace.none,
  }

  /**
   * All Workspaces that have bundles
   */
  @computed
  get workspaces() {
    return this.workspaceSearcher.filteredItems.map((x) => x.workspace)
  }

  @computed
  get canManageBundles() {
    return this.getWorkspaceMemberships().some((wms) =>
      wms.member.hasPermission('ADMIN_MANAGE_GLOBAL_INVENTORY_ITEMS'),
    )
  }

  @computed
  get canSearch() {
    return this.workspaceSearcher.activeWorkspaces.length >= 5
  }

  @computed
  get workspacesUseDropdown() {
    return this.canManageBundles
      ? this.canSearch || this.workspaces.length === 1
      : this.workspaces.length > 1
  }

  /**
   * Set the archived status tab.
   */
  @action.bound
  setCurrentArchivedStatusTab(archivedStatusTab: BundleArchivedFilterOptions) {
    if (archivedStatusTab === this.currentArchivedStatusTab) {
      return
    }

    this.currentArchivedStatusTab = archivedStatusTab
    this.searcher.setCurrentArchivedStatusFilter(archivedStatusTab)
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none ||
      !this.workspaces.find((ws) => ws.id === workspace.id)
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'Workspace' }

    this.searcher.setWorkspace(
      workspace === Workspace.none ? undefined : workspace,
    )

    this.workspaceVMs.items
      .find((ws) => ws.id === workspace.id)
      ?.selectedBundlesId.clear()
  }

  async refreshBundles(workspace?: Workspace) {
    await this.workspaceSearcher.refreshWorkspacesWithBundles()
    if (workspace) {
      this.selectWorkspaceForFiltering(workspace)
    }
    await this.searcher.refreshItems()
  }

  activate = task.resolved(async () => {
    await this.activateWorkspaceSearcher()
    if (this.canManageBundles) {
      this.selectWorkspaceForFiltering(Workspace.none)
    } else if (this.workspaces.length > 0) {
      this.selectWorkspaceForFiltering(this.workspaces[0])
    }

    this.subscription = this.workspaceSelectorUpdates$.subscribe()

    await this.activateSearcher()

    this.workspaceSelectorViewModel.activate()

    this.workspaceVMs.activate()
  })

  deactivate() {
    this.subscription.unsubscribe()
    this.workspaceSearcher.deactivate()
    this.workspaceSelectorViewModel.deactivate()
    this.workspaceVMs.deactivate()
  }

  getWorkspaceBundleVM(workspace: Workspace) {
    return this.workspaceVMs.items.find((vm) => vm.id === workspace.id)
  }
}
